import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AuthShell from "./AuthShell";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { signIn, user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!loading && user && profile) {
    let target = "/portal";
    if (profile.role === "professional") target = "/app";
    if (profile.role === "superadmin") target = "/admin";
    
    return <Navigate to={location.state?.from?.pathname || target} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await signIn({ email, password });
    } catch (e2) {
      setErr(translateAuthError(e2));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell
      title="Bem-vinda de volta"
      subtitle="Entre com seu e-mail e senha para acessar o painel."
      footer={
        <div className="text-sm text-[hsl(var(--text-secondary))]">
          Ainda não tem conta?{" "}
          <Link to="/cadastro/profissional" className="ds-link">Sou profissional</Link>
          {" · "}
          <Link to="/cadastro/paciente" className="ds-link">Sou paciente</Link>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-mail">
          <input data-testid="login-email" type="email" required className="ds-input"
                 value={email} onChange={e => setEmail(e.target.value)} placeholder="seu@email.com" />
        </Field>
        <div className="block">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-[hsl(var(--text-secondary))]">Senha</span>
            <Link to="/forgot-password" className="text-xs font-semibold text-brand hover:underline">Esqueceu a senha?</Link>
          </div>
          <div className="relative">
            <input data-testid="login-password" type={showPwd ? "text" : "password"} required className="ds-input pr-11 h-11"
                   value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
            <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]" aria-label="Mostrar senha">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {err && <div className="text-sm text-[hsl(var(--danger-text))] bg-[hsl(var(--danger-bg))] rounded-md px-3 py-2" data-testid="login-error">{err}</div>}
        <button data-testid="login-submit" disabled={busy} className="ds-btn-primary w-full h-11">
          {busy ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </AuthShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export function translateAuthError(e) {
  const msg = (e?.message || "").toLowerCase();
  if (msg.includes("invalid login")) return "E-mail ou senha incorretos.";
  if (msg.includes("email not confirmed")) return "Confirme seu e-mail antes de entrar.";
  if (msg.includes("user already registered")) return "Já existe uma conta com esse e-mail.";
  if (msg.includes("password should be at least")) return "A senha deve ter ao menos 6 caracteres.";
  return e?.message || "Não foi possível concluir. Tente novamente.";
}
